/**
 * Strip Legacy Integrations Tool
 *
 * Removes the deprecated top-level `integrations:` block from stackwright.yml.
 * The authoritative source for integration config is stackwright.integrations.yml —
 * this tool cleans up legacy entries left by older api-otter versions.
 *
 * Algorithm: textual line-based edit.
 * - Preserves all comments, line endings, and other top-level key ordering.
 * - Idempotent: no-op if the block is absent.
 * - Never touches stackwright.integrations.yml — that's the GOOD file.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { existsSync, readFileSync, writeFileSync } from 'fs';
import { join } from 'path';

// ---------------------------------------------------------------------------
// Types (exported for direct testing)
// ---------------------------------------------------------------------------

export interface StripLegacyIntegrationsInput {
  projectRoot: string;
}

export interface StripLegacyIntegrationsResult {
  changed: boolean;
  removedEntries: number;
  message: string;
}

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

export function handleStripLegacyIntegrations(
  input: StripLegacyIntegrationsInput
): StripLegacyIntegrationsResult {
  const { projectRoot } = input;
  const ymlPath = join(projectRoot, 'stackwright.yml');
  const yamlPath = join(projectRoot, 'stackwright.yaml');

  // stackwright.yml absent — check for .yaml sibling to give a helpful message
  if (!existsSync(ymlPath)) {
    if (existsSync(yamlPath)) {
      return {
        changed: false,
        removedEntries: 0,
        message: 'stackwright.yml not found (only stackwright.yaml exists — please rename)',
      };
    }
    return {
      changed: false,
      removedEntries: 0,
      message: 'stackwright.yml not found — no-op.',
    };
  }

  const raw = readFileSync(ymlPath, 'utf-8');

  // Detect and normalise line endings so all regex/string ops use LF internally
  const isCRLF = raw.includes('\r\n');
  const content = isCRLF ? raw.replace(/\r\n/g, '\n') : raw;

  const lines = content.split('\n');

  // Find top-level `integrations:` key (column 0 only — not inside a comment)
  const startIdx = lines.findIndex((line) => /^integrations\s*:/.test(line));

  if (startIdx === -1) {
    return {
      changed: false,
      removedEntries: 0,
      message: 'No legacy integrations[] block present.',
    };
  }

  // Scan forward from startIdx+1 to find the end of the block.
  // Stop at the first line that is non-empty and does not start with
  // whitespace or '-' (sequence marker at col 0). Comments at col 0 ('#')
  // are treated as top-level markers and stop the scan — preserving them.
  let endIdx = lines.length;
  for (let i = startIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.length === 0) continue; // blank — skip
    if (line[0] === ' ' || line[0] === '\t') continue; // indented — within block
    if (line[0] === '-') continue; // col-0 sequence item — within block
    // Non-empty, non-indented, non-dash → top-level key or col-0 comment
    endIdx = i;
    break;
  }

  // Count entries: look for `  - type:` lines in the removed slice
  const removedSlice = lines.slice(startIdx, endIdx);
  const entryMatches = removedSlice.join('\n').match(/^\s+- type:/gm);
  const removedEntries = entryMatches ? entryMatches.length : 0;

  // Build output: everything before startIdx + everything from endIdx onward
  const outputLines = [...lines.slice(0, startIdx), ...lines.slice(endIdx)];
  let outputContent = outputLines.join('\n');

  // Preserve trailing newline: if original ended with '\n', ensure output does too
  if (content.endsWith('\n') && !outputContent.endsWith('\n')) {
    outputContent += '\n';
  }

  // Restore original line-ending style
  const outputRaw = isCRLF ? outputContent.replace(/\n/g, '\r\n') : outputContent;

  writeFileSync(ymlPath, outputRaw, 'utf-8');

  return {
    changed: true,
    removedEntries,
    message: `Stripped ${removedEntries} legacy integration entries from stackwright.yml`,
  };
}

// ---------------------------------------------------------------------------
// MCP registration
// ---------------------------------------------------------------------------

export function registerStripLegacyIntegrationsTool(server: McpServer): void {
  server.tool(
    'stackwright_pro_strip_legacy_integrations',
    'Removes the deprecated top-level integrations[] block from stackwright.yml. The authoritative source for integration config is stackwright.integrations.yml — this tool cleans up legacy entries from older raft runs. Idempotent: no-op if no legacy block present. Preserves all other top-level keys, comments, and formatting.',
    {
      projectRoot: z.string().describe('Absolute path to the project root directory'),
    },
    async ({ projectRoot }) => {
      const result = handleStripLegacyIntegrations({ projectRoot });
      return {
        content: [{ type: 'text', text: JSON.stringify(result, null, 2) }],
      };
    }
  );
}
